import { useState, useMemo } from "react";
import { Button, Select, Tag, Empty, Spin, Space, Segmented } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Header from "../_shared/components/Header";
import {
  useGetStaffMembersQuery,
  useGetStaffDeployedWeeksQuery,
  useGetStaffWeeklyOverviewQuery,
} from "../../../redux/api/scheduling";
import MachineGroupWeekTimeline from "./MachineGroupWeekTimeline";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getSunday(d: dayjs.Dayjs) {
  return d.subtract(d.day(), "day").startOf("day");
}

export default function StaffWeeklyOverview() {
  const [weekStart, setWeekStart] = useState(() => getSunday(dayjs()));
  const [profileFilter, setProfileFilter] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"By Staff" | "By Machine Group">("By Staff");

  const weekStartStr = weekStart.format("YYYY-MM-DD");

  const { data: staffData, isLoading: staffLoading } = useGetStaffMembersQuery({});
  const { data: deployedData, isLoading: deployedLoading } = useGetStaffDeployedWeeksQuery({
    week_start_date__gte: weekStartStr,
    week_start_date__lte: weekStartStr,
  } as any);
  const { data: overviewData, isLoading: overviewLoading } = useGetStaffWeeklyOverviewQuery({
    week_start_date: weekStartStr,
  });

  const allStaff: any[] = staffData?.results || staffData || [];
  const deployed: any[] = deployedData?.results || deployedData || [];
  const { data: prevOverviewData } = useGetStaffWeeklyOverviewQuery(
    { week_start_date: weekStart.subtract(7, "day").format("YYYY-MM-DD") },
    { skip: viewMode !== "By Machine Group" }
  );

  const groupsData: any[] = overviewData?.groups || [];
  const prevGroupsData: any[] | undefined = prevOverviewData?.groups;
  const staffVacations: any[] = overviewData?.staff_vacations || [];

  const activeStaff = useMemo(() => {
    let filtered = allStaff.filter((s: any) => s.is_active);
    if (profileFilter) {
      filtered = filtered.filter((s: any) => s.profile_name === profileFilter);
    }
    return filtered.sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));
  }, [allStaff, profileFilter]);

  const profiles = useMemo(() => {
    const set = new Set<string>();
    allStaff.forEach((s: any) => { if (s.profile_name) set.add(s.profile_name); });
    return Array.from(set).sort();
  }, [allStaff]);

  const deployedByStaff = useMemo(() => {
    const map = new Map<number, any>();
    deployed.forEach((dw: any) => {
      if (dw.week_start_date === weekStartStr) {
        map.set(dw.staff_member, dw);
      }
    });
    return map;
  }, [deployed, weekStartStr]);

  // Vacation lookup: staff_id → set of day names on vacation
  const vacationByStaff = useMemo(() => {
    const map = new Map<number, Set<string>>();
    staffVacations.forEach((sv: any) => {
      map.set(sv.staff_id, new Set(sv.vacation_days || []));
    });
    return map;
  }, [staffVacations]);

  function parseDayCoverage(dw: any): Map<string, string[]> {
    const dayMap = new Map<string, string[]>();
    if (!dw?.shift_lines) return dayMap;
    for (const line of dw.shift_lines) {
      const match = line.match(/^(\w+)(?:-(\w+))?\s+(.+)$/);
      if (!match) continue;
      const startDay = match[1];
      const endDay = match[2];
      const timeRange = match[3];
      if (endDay) {
        const startIdx = DAY_LABELS.indexOf(startDay);
        const endIdx = DAY_LABELS.indexOf(endDay);
        if (startIdx >= 0 && endIdx >= 0) {
          for (let i = startIdx; i <= endIdx; i++) {
            const entries = dayMap.get(DAY_LABELS[i]) || [];
            entries.push(timeRange);
            dayMap.set(DAY_LABELS[i], entries);
          }
        }
      } else {
        const entries = dayMap.get(startDay) || [];
        entries.push(timeRange);
        dayMap.set(startDay, entries);
      }
    }
    return dayMap;
  }

  const isLoading = staffLoading || deployedLoading || overviewLoading;

  return (
    <div className="w-full p-4">
      <Header title="Staff Weekly Overview" />

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <Segmented
          options={["By Staff", "By Machine Group"]}
          value={viewMode}
          onChange={(val) => setViewMode(val as any)}
        />
        <Space>
          <Button icon={<LeftOutlined />} onClick={() => setWeekStart(weekStart.subtract(1, "week"))} />
          <span style={{ fontWeight: 600, fontSize: 14, minWidth: 200, textAlign: "center" }}>
            {weekStart.format("MMM D")} – {weekStart.add(6, "day").format("MMM D, YYYY")}
          </span>
          <Button icon={<RightOutlined />} onClick={() => setWeekStart(weekStart.add(1, "week"))} />
          <Button size="small" onClick={() => setWeekStart(getSunday(dayjs()))}>Today</Button>
        </Space>
        {viewMode === "By Staff" && (
          <Select
            placeholder="Filter by profile..."
            allowClear
            style={{ width: 200 }}
            value={profileFilter}
            onChange={(val) => setProfileFilter(val || null)}
            options={profiles.map((p) => ({ value: p, label: p }))}
          />
        )}
      </div>

      {isLoading ? (
        <div style={{ textAlign: "center", padding: 80 }}><Spin size="large" /></div>
      ) : viewMode === "By Staff" ? (
        /* ═══════════════════ BY STAFF VIEW ═══════════════════ */
        <div style={{ overflowX: "auto", border: "1px solid #e8e8e8", borderRadius: 6 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#fafafa" }}>
                <th style={{ ...thStyle, width: 160, position: "sticky", left: 0, background: "#fafafa", zIndex: 2 }}>Staff</th>
                <th style={{ ...thStyle, width: 70 }}>Profile</th>
                {DAY_LABELS.map((day, i) => (
                  <th key={day} style={{ ...thStyle, minWidth: 130, background: (i === 0 || i === 6) ? "#f5f5f5" : "#fafafa" }}>
                    {day} {weekStart.add(i, "day").format("M/D")}
                  </th>
                ))}
                <th style={{ ...thStyle, width: 80 }}>Hours</th>
                <th style={{ ...thStyle, width: 160 }}>Groups</th>
              </tr>
            </thead>
            <tbody>
              {activeStaff.map((staff: any) => {
                const dw = deployedByStaff.get(staff.id);
                const dayCoverage = parseDayCoverage(dw);
                const hasDeployment = !!dw && !dw.is_cleared;
                const machineGroups: string[] = dw?.machine_groups || [];
                const vacDays = vacationByStaff.get(staff.id);

                return (
                  <tr key={staff.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ ...tdStyle, fontWeight: 600, position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>
                      {staff.name}
                      {vacDays && vacDays.size > 0 && (
                        <Tag color="red" style={{ marginLeft: 4, fontSize: 9 }}>Vacation</Tag>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {staff.profile_initial ? <Tag color="blue">{staff.profile_initial}</Tag> : "—"}
                    </td>
                    {DAY_LABELS.map((day, i) => {
                      const shifts = dayCoverage.get(day) || [];
                      const isWeekend = i === 0 || i === 6;
                      const hasShift = shifts.length > 0;
                      const isGap = hasDeployment && !hasShift && !isWeekend;
                      const onVacation = vacDays?.has(day);

                      return (
                        <td key={day} style={{
                          ...tdStyle,
                          background: onVacation ? "#fff1f0" : isGap ? "#fff7e6" : isWeekend ? "#fafafa" : hasShift ? "#f6ffed" : "#fff",
                        }}>
                          {onVacation && <div style={{ color: "#ff4d4f", fontSize: 10, fontWeight: 600 }}>On Vacation</div>}
                          {shifts.length > 0 ? (
                            shifts.map((s, idx) => (
                              <div key={idx} style={{
                                whiteSpace: "nowrap", fontSize: 11,
                                textDecoration: onVacation ? "line-through" : undefined,
                                color: onVacation ? "#999" : undefined,
                              }}>{s}</div>
                            ))
                          ) : hasDeployment && !isWeekend && !onVacation ? (
                            <span style={{ color: "#ff4d4f", fontSize: 10 }}>No shift</span>
                          ) : !onVacation ? (
                            <span style={{ color: "#d9d9d9" }}>—</span>
                          ) : null}
                        </td>
                      );
                    })}
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: 500 }}>{dw?.shift_total_hours || "—"}</td>
                    <td style={tdStyle}>
                      {machineGroups.length > 0 ? (
                        <Space size={[2, 2]} wrap>
                          {machineGroups.map((g) => <Tag key={g} color="geekblue" style={{ fontSize: 10, margin: 0 }}>{g}</Tag>)}
                        </Space>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* ═══════════════════ BY MACHINE GROUP VIEW ═══════════════════ */
        groupsData.length === 0 ? (
          <Empty description="No machine group data" />
        ) : (
          <MachineGroupWeekTimeline
            groups={groupsData}
            prevGroups={prevGroupsData}
            weekStart={weekStart}
          />
        )
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: "8px 6px",
  textAlign: "left",
  borderBottom: "2px solid #d9d9d9",
  borderRight: "1px solid #f0f0f0",
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "6px",
  borderRight: "1px solid #f0f0f0",
  verticalAlign: "top",
};
